import styled from 'styled-components';
import { LoaderModal, PageWrapper } from 'components';
import { Header } from './components/Header';
import { useGetVehicleId } from '../../CreateVehicle/hooks/useGetVehicleId';
import { useGetVehicleByIdQuery } from '../../../services/api/upload-vehicle';
import { ImageCarousel } from './components/ImageCarousel';
import { VehicleSections } from './components/VehicleSections';
import React, { useRef } from 'react';
import ReactImageGallery from 'react-image-gallery';
import { VehicleSaleBlock } from './components/VehicleSaleBlock';
import { ListerInformation } from './components/ListerInformation';
import { VehicleInfo } from './components/VehicleInfo';
import { AutoCheckReport } from './components/AutoCheckReport';
import { OfferMessages } from './components/OfferMessages';
import { UserActions } from './components/UserActions';
import AuctionSummarySection from './components/AuctionSummarySection';
import { VehicleCharacteristics } from './components/VehicleCharacteristics';
import { IncreaseMaxBid } from './components/IncreaseMaxBid';
import { IncreaseMaxOffer } from './components/IncreaseMaxOffer';
import { AuctionEndTime } from './components/AuctionEndTime';
import { useCurrentUser } from '../../../hooks/selectors';
import { InspectorComments } from './components/InspectorComments';
import { BidsHistory } from './components/BidsHistory';
import { useViewVehicleSocketEffect } from '../../../services/socket/handleViewVehicleEvent';
import { AskAQuestion } from './components/AskAQuestion';
import { Breadcrumbs } from './components/Breadcrumbs';
import { Vehicle } from '../../../types/vehicle';
import { useWindowSize } from 'usehooks-ts';
import { MyBids } from './components/MyBids';
import { AwardControls } from './components/UserActions/Admin/AwardControls';
import { useInitImages } from './hooks/useInitImages';
import { useViewedVehicle } from './hooks/useViewedVehicle';
import { SidePanel } from './components/SidePanel';
import { FullScreenImageDialog } from './components/FullScreenImageDialog';
import { IncreaseAlternativeOffer } from './components/IncreaseAlternativeOffer';
import { VisibleBuyersBlock } from './components/VisibleBuyersBlock';
import { PrintView } from './components/PrintView';

const StyledPageWrapper = styled(PageWrapper)`
  padding: 70px 12px 150px 100px;
  display: flex;
  margin: 0;
  position: relative;
  flex-direction: column;
  gap: 20px;
  width: calc(100% - 230px);

  @media only screen and (min-width: 1800px) {
    margin: 0 auto;
    padding-left: 0;
  }

  @media (max-width: 1024px) {
    padding: 12px 16px 16px 16px;
    width: 100%;
    gap: 8px;
    margin-top: 60px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const MainPart = styled.div`
  display: flex;
  gap: 16px;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 22px;
  min-width: 440px;
  flex-grow: 1;
`;

const BottomPart = styled.div`
  display: flex;
  gap: 8px;
  & > div {
    flex: 1 1 100%;
    overflow: hidden;
  }
`;

const Wrapper = styled.div`
  position: relative;
`;

export const ViewVehicle = () => {
  const vehicleId = useGetVehicleId();
  const { data: vehicle, isLoading } = useGetVehicleByIdQuery(vehicleId, { skip: !vehicleId });

  const user = useCurrentUser();
  const imageGalleryRef = useRef<ReactImageGallery>(null);

  useInitImages();
  useViewVehicleSocketEffect(vehicle?.id);

  const { width = 0 } = useWindowSize();

  if (!vehicle) return <LoaderModal modalId={'vehicle_loading'} forceOpen={isLoading} />;

  if (width < 1024) {
    return <MobileView vehicle={vehicle} imageGalleryRef={imageGalleryRef} />;
  }

  return (
    <Wrapper>
      <Header />
      <StyledPageWrapper>
        <MainPart>
          <Left>
            <ImageCarousel imageGalleryRef={imageGalleryRef} />
            <VehicleCharacteristics />
          </Left>
          <Right>
            <VehicleInfo />
            <AutoCheckReport />
            <OfferMessages />
            <UserActions />
            {user.role === 'admin' && <AwardControls />}
            <IncreaseMaxBid />
            <IncreaseMaxOffer />
            <IncreaseAlternativeOffer />
            <AuctionSummarySection />
            <BidsHistory />
            <VisibleBuyersBlock />
            <AuctionEndTime />
          </Right>
        </MainPart>
        <VehicleSections />
        <VehicleSaleBlock />
        <BottomPart>
          <InspectorComments />
          <ListerInformation />
        </BottomPart>
      </StyledPageWrapper>
      <LoaderModal modalId={'vehicle_loading'} forceOpen={isLoading} />
      <FullScreenImageDialog imageGalleryRef={imageGalleryRef} />
      <SidePanel />
      <AskAQuestion />
      <PrintView />
    </Wrapper>
  );
};

const MobileView = ({ imageGalleryRef }: { vehicle: Vehicle; imageGalleryRef: React.RefObject<ReactImageGallery> }) => {
  const user = useCurrentUser();
  const mobileVehicle = useViewedVehicle();
  return (
    <Wrapper>
      <Header />
      <StyledPageWrapper>
        <MyBids />
        <ImageCarousel imageGalleryRef={imageGalleryRef} />
        <VehicleInfo />
        <UserActions />
        {user.role === 'admin' && <AwardControls />}
        <AutoCheckReport />
        <OfferMessages />
        <IncreaseMaxBid />
        <IncreaseMaxOffer />
        <IncreaseAlternativeOffer />
        <AuctionSummarySection />
        <BidsHistory />
        <VisibleBuyersBlock />
        <AuctionEndTime />
        <VehicleCharacteristics />
        <VehicleSections />
        <VehicleSaleBlock />
        <InspectorComments />
        <ListerInformation />
        <AskAQuestion />
      </StyledPageWrapper>
      <FullScreenImageDialog imageGalleryRef={imageGalleryRef} />
      <PrintView />
    </Wrapper>
  );
};
